/* Nutrise Cart Discount v1.27 */
class CartDiscountCode extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('[data-discount-form]');
    this.input = this.querySelector('[name="discount"]');
    this.button = this.querySelector('button[type="submit"]');
    this.message = this.querySelector('[role="status"]');
    this.activeDiscounts = this.querySelector('[data-active-discounts]');
    this.activeDiscountList = this.querySelector('[data-active-discount-list]');

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    if (this.activeDiscountList) {
      this.activeDiscountList.addEventListener('click', this.onRemove.bind(this));
    }

    this.loadActiveCodes();
  }

  async onSubmit(event) {
    event.preventDefault();

    const code = this.input?.value.trim();

    if (!code) {
      this.showMessage('Bitte gib einen Rabattcode ein.', 'error');
      this.input?.focus();
      return;
    }

    this.setLoading(true);
    this.showMessage('', '');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const currentCartResponse = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!currentCartResponse.ok) {
        throw new Error('Der Warenkorb konnte nicht geladen werden.');
      }

      const currentCart = await currentCartResponse.json();
      const existingCodes = this.getApplicableCodes(currentCart);
      const codeAlreadyApplied = existingCodes.some(
        (existingCode) => this.normalize(existingCode) === this.normalize(code)
      );

      if (codeAlreadyApplied) {
        this.renderActiveCodes(existingCodes);
        this.showMessage('', '');
        this.setLoading(false);
        return;
      }

      const discountCodes = [...existingCodes, code].filter(
        (discountCode, index, codes) =>
          codes.findIndex((candidate) => this.normalize(candidate) === this.normalize(discountCode)) === index
      );

      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: discountCodes.join(','),
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: `${window.location.pathname}${window.location.search}`,
        }),
      });

      const updatedCart = await response.json();

      if (!response.ok) {
        throw new Error(updatedCart.description || updatedCart.message || 'Der Rabattcode konnte nicht angewendet werden.');
      }

      const appliedCode = this.findDiscountCode(updatedCart, code);
      const codeWasApplied = appliedCode
        ? appliedCode.applicable !== false
        : this.hasMatchingDiscountTitle(updatedCart, code) || updatedCart.total_price < currentCart.total_price;
      const activeCodes = this.getApplicableCodes(updatedCart);

      if (codeWasApplied && !activeCodes.some((activeCode) => this.normalize(activeCode) === this.normalize(code))) {
        activeCodes.push(code);
      }

      this.renderCart(updatedCart);

      requestAnimationFrame(() => {
        const nextDiscountElement = document.querySelector('cart-drawer cart-discount-code');

        if (!nextDiscountElement) return;

        if (codeWasApplied) {
          nextDiscountElement.revealMessage('Rabattcode wurde angewendet.', 'success', '', activeCodes);
        } else {
          nextDiscountElement.revealMessage(
            'Der Rabattcode ist ungültig oder die Bedingungen sind nicht erfüllt.',
            'error',
            code,
            activeCodes
          );
        }
      });
    } catch (error) {
      this.showMessage(error.message || 'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.', 'error');
      this.setLoading(false);
    }
  }

  async onRemove(event) {
    const removeButton = event.target.closest('[data-remove-discount]');
    if (!removeButton || !this.activeDiscountList?.contains(removeButton)) return;

    event.preventDefault();
    event.stopPropagation();

    const code = removeButton.dataset.removeDiscount?.trim();
    if (!code) return;

    this.setRemoving(true, removeButton);
    this.showMessage('', '');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const currentCartResponse = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!currentCartResponse.ok) {
        throw new Error('Der Warenkorb konnte nicht geladen werden.');
      }

      const currentCart = await currentCartResponse.json();
      const existingCodes = this.getApplicableCodes(currentCart);
      const remainingCodes = existingCodes.filter(
        (existingCode) => this.normalize(existingCode) !== this.normalize(code)
      );

      if (remainingCodes.length === existingCodes.length) {
        this.renderActiveCodes(existingCodes);
        this.showMessage('Dieser Rabattcode ist nicht mehr aktiv.', 'error');
        this.setRemoving(false);
        return;
      }

      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: remainingCodes.join(','),
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: `${window.location.pathname}${window.location.search}`,
        }),
      });

      const updatedCart = await response.json();

      if (!response.ok) {
        throw new Error(updatedCart.description || updatedCart.message || 'Der Rabattcode konnte nicht entfernt werden.');
      }

      const activeCodes = this.getApplicableCodes(updatedCart);
      this.renderCart(updatedCart);

      requestAnimationFrame(() => {
        const nextDiscountElement = document.querySelector('cart-drawer cart-discount-code');
        if (!nextDiscountElement) return;

        nextDiscountElement.revealMessage('Rabattcode wurde entfernt.', 'success', '', activeCodes);
      });
    } catch (error) {
      this.showMessage(error.message || 'Der Rabattcode konnte nicht entfernt werden. Bitte versuche es erneut.', 'error');
      this.setRemoving(false);
    }
  }

  getApplicableCodes(cart) {
    if (!Array.isArray(cart?.discount_codes)) return [];

    return cart.discount_codes
      .filter((discountCode) => discountCode?.code && discountCode.applicable !== false)
      .map((discountCode) => discountCode.code);
  }

  async loadActiveCodes() {
    if (!this.activeDiscounts || !this.activeDiscountList) return;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return;

      const cart = await response.json();
      this.renderActiveCodes(this.getApplicableCodes(cart));
    } catch (error) {
      // The discount form remains fully usable if the status request fails.
    }
  }

  renderActiveCodes(codes) {
    if (!this.activeDiscounts || !this.activeDiscountList) return;

    const uniqueCodes = (Array.isArray(codes) ? codes : [])
      .map((code) => String(code || '').trim())
      .filter(Boolean)
      .filter(
        (code, index, allCodes) =>
          allCodes.findIndex((candidate) => this.normalize(candidate) === this.normalize(code)) === index
      );

    this.activeDiscountList.replaceChildren();

    uniqueCodes.forEach((code) => {
      const item = document.createElement('li');
      const check = document.createElement('span');
      const codeLabel = document.createElement('span');
      const status = document.createElement('span');
      const removeButton = document.createElement('button');

      item.className = 'nutrise-discount__active-item';
      item.setAttribute('aria-label', `Rabattcode ${code} ist aktiv`);

      check.className = 'nutrise-discount__active-check';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '✓';

      codeLabel.className = 'nutrise-discount__active-code';
      codeLabel.textContent = code;

      status.className = 'nutrise-discount__active-status';
      status.textContent = 'aktiv';

      removeButton.type = 'button';
      removeButton.className = 'nutrise-discount__remove';
      removeButton.dataset.removeDiscount = code;
      removeButton.setAttribute('aria-label', `Rabattcode ${code} entfernen`);
      removeButton.title = 'Rabattcode entfernen';
      removeButton.textContent = '×';

      item.append(check, codeLabel, status, removeButton);
      this.activeDiscountList.append(item);
    });

    const hasActiveCode = uniqueCodes.length > 0;

    this.activeDiscounts.hidden = !hasActiveCode;
    if (this.form) this.form.hidden = hasActiveCode;
    this.classList.toggle('has-active-discount', hasActiveCode);
  }

  findDiscountCode(cart, code) {
    if (!Array.isArray(cart?.discount_codes)) return null;

    return (
      cart.discount_codes.find(
        (discountCode) => discountCode?.code && this.normalize(discountCode.code) === this.normalize(code)
      ) || null
    );
  }

  hasMatchingDiscountTitle(cart, code) {
    const titles = [];

    if (Array.isArray(cart?.cart_level_discount_applications)) {
      cart.cart_level_discount_applications.forEach((discount) => {
        if (discount?.title) titles.push(discount.title);
      });
    }

    if (Array.isArray(cart?.items)) {
      cart.items.forEach((item) => {
        if (Array.isArray(item?.discounts)) {
          item.discounts.forEach((discount) => {
            if (discount?.title) titles.push(discount.title);
          });
        }
      });
    }

    return titles.some((title) => this.normalize(title) === this.normalize(code));
  }

  renderCart(cart) {
    const cartDrawer = document.querySelector('cart-drawer');
    const hasRequiredSections = cart?.sections?.['cart-drawer'] && cart?.sections?.['cart-icon-bubble'];

    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function' || !hasRequiredSections) {
      window.location.reload();
      return;
    }

    cartDrawer.renderContents(cart);
  }

  revealMessage(text, type, value, activeCodes) {
    if (this.input) this.input.value = value;
    if (Array.isArray(activeCodes)) this.renderActiveCodes(activeCodes);

    const hasActiveCode = Array.isArray(activeCodes) && activeCodes.length > 0;
    const codeWasRemoved = type === 'success' && text === 'Rabattcode wurde entfernt.';

    this.showMessage(type === 'success' && hasActiveCode ? '' : text, type, codeWasRemoved);
    this.setLoading(false);

    if (type === 'error') {
      this.input?.focus();
    }
  }

  showMessage(text, type, visuallyHidden = false) {
    if (!this.message) return;

    this.message.textContent = text;
    this.message.classList.remove('is-error', 'is-success', 'is-visually-hidden');

    if (type === 'error') this.message.classList.add('is-error');
    if (type === 'success') this.message.classList.add('is-success');
    if (visuallyHidden && text) this.message.classList.add('is-visually-hidden');
  }

  setLoading(isLoading) {
    if (!this.button || !this.input) return;

    this.button.disabled = isLoading;
    this.input.disabled = isLoading;
    this.button.classList.toggle('is-loading', isLoading);
    this.form?.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  setRemoving(isLoading, activeButton = null) {
    if (this.button) this.button.disabled = isLoading;
    if (this.input) this.input.disabled = isLoading;

    this.querySelectorAll('[data-remove-discount]').forEach((button) => {
      button.disabled = isLoading;
      button.classList.toggle('is-loading', isLoading && button === activeButton);
    });

    this.form?.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  normalize(value) {
    return String(value || '').trim().toLocaleLowerCase();
  }
}

if (!customElements.get('cart-discount-code')) {
  customElements.define('cart-discount-code', CartDiscountCode);
}
